
import Enumerable from "linq";
import { Component, Mixins } from "vue-property-decorator";
import { DateDuration } from "../../../../../base/domain/system";
import { ObjectUtility } from "../../../../../base/utilities";
import { EditCommandManageableMixin } from "../../../../../base/components/system/edit/mixins";
import {
  FormatStructureEditor,
  FormatStructureEditorStorePresenter,
  IEditableFormatReferenceFile,
  FormatPageStructureEditor,
  FormatPagesStructureEditor,
  IFormatObjectEditable,
  IEditableFormatSetting,
  IEditableFormatPageStructure,
  WriteMethodKinds,
  IFormatAlert,
  IEditableFormatQuestionnaire,
} from "../../../../domain/editableFormat";

@Component
export default class FormatStructureEditableMixin extends Mixins(EditCommandManageableMixin) {
  // ModelEditableMixinと同時に使う事.ModelEditableMixin利用APIの宣言.
  writePropertyWhenValid: (writer: (source: FormatStructureEditor) => void) => void;
  readPropertyOrDefault: <T>(reader: (source: FormatStructureEditor) => T, defaultValue: T) => T;

  get storePresenter(): FormatStructureEditorStorePresenter {
    return this.readPropertyOrDefault(s => s.getStorePresenter(), null);
  }

  get pages(): FormatPageStructureEditor[] {
    return this.readPropertyOrDefault(s => s.getPages(), []);
  }

  get pageCount(): number {
    return this.pages.length;
  }

  get isSinglePage(): boolean {
    return (this.pageCount === 1);
  }

  createNewPage(source: FormatPageStructureEditor): FormatPageStructureEditor {
    return this.readPropertyOrDefault(s => s.createNewPage(this.clonePageSource(source.getSource())), null);
  }

  private clonePageSource(source: IEditableFormatPageStructure): IEditableFormatPageStructure {
    const result = ObjectUtility.copyDeeply(source) as IEditableFormatPageStructure;
    result.objects = [];
    result.layer = {
      formatId: null,
      sectionNo: null,
      imageGuid: null,
      location: {
        x: 0,
        y: 0,
      },
      size: {
        width: 0,
        height: 0,
      },
      fileName: "",
    };
    return result;
  }

  addPage(source: FormatPageStructureEditor): void {
    this.writePropertyWhenValid(s => s.addPage(source));
  }

  removeSelectedPages() {
    this.writePropertyWhenValid(s => s.removePages(s.getSelectedPages().concat().reverse()));
  }

  get isRaisable() {
    return (0 < this.raisableCount);
  }

  get raisableCount(): number {
    return this.hasSelectedPages
      ? Enumerable.from(this.pages).select((p, i) => ({ p, i })).first(d => d.p.isSelected()).i
      : 0;
  }

  raiseSelectedPages(): void {
    this.shiftSelectedPages(-1);
  }

  raiseSelectedPagesToTop(): void {
    this.shiftSelectedPages(-this.raisableCount);
  }

  shiftSelectedPages(count: number): void {
    if (0 < count) {
      this.writePropertyWhenValid(s => s.shiftPages(s.getSelectedPages().concat().reverse(), count));
    } else {
      this.writePropertyWhenValid(s => s.shiftPages(s.getSelectedPages().concat(), count));
    }
  }

  get isLowerable() {
    return (0 < this.lowerableCount);
  }

  get lowerableCount(): number {
    return this.hasSelectedPages
      ? Enumerable.from(this.pages).reverse().select((p, i) => ({ p, i })).first(d => d.p.isSelected()).i
      : 0;
  }

  lowerSelectedPages(): void {
    this.shiftSelectedPages(1);
  }

  lowerSelectedPagesToBottom(): void {
    this.shiftSelectedPages(this.lowerableCount);
  }

  get selectedPages(): FormatPageStructureEditor[] {
    return this.readPropertyOrDefault(s => s.getSelectedPages(), []);
  }

  reselectPages(...pages: FormatPageStructureEditor[]): void {
    this.writePropertyWhenValid(s => {
      s.deselectAllPages();
      s.selectPages(...pages);
    });
  }

  selectPages(...pages: FormatPageStructureEditor[]): void {
    this.writePropertyWhenValid(s => s.selectPages(...pages));
  }

  get selectedPageCount(): number {
    return this.selectedPages.length;
  }

  get hasSelectedPages(): boolean {
    return !this.areAllPagesDeselected;
  }

  get areAllPagesSelected(): boolean {
    return (this.pageCount <= this.selectedPageCount);
  }

  get areAllPagesDeselected(): boolean {
    return (this.selectedPageCount <= 0);
  }

  get isSinglePageSelected(): boolean {
    return (this.selectedPageCount === 1);
  }

  get selectedSinglePage(): FormatPageStructureEditor {
    return this.isSinglePageSelected
      ? this.selectedPages[0]
      : null;
  }

  set selectedSinglePage(page: FormatPageStructureEditor) {
    this.writePropertyWhenValid(s => s.updatePage(page));
  }

  get selectedMultiplePages(): FormatPagesStructureEditor {
    return this.hasSelectedPages
      ? this.readPropertyOrDefault(s => new FormatPagesStructureEditor(this.selectedPages), null)
      : null;
  }

  set selectedMultiplePages(editor: FormatPagesStructureEditor) {
    this.writePropertyWhenValid(s => s.updatePages(editor.source()));
  }

  get selectedPagesEditor(): FormatPageStructureEditor | FormatPagesStructureEditor {
    return this.isSinglePageSelected
      ? this.selectedSinglePage
      : this.selectedMultiplePages;
  }

  set selectedPagesEditor(source: FormatPageStructureEditor | FormatPagesStructureEditor) {
    if (source instanceof FormatPagesStructureEditor) {
      this.selectedMultiplePages = source;
    } else {
      this.selectedSinglePage = source;
    }
  }

  writeSelectedPagesEditorWhenValid(writer: (source: FormatPageStructureEditor | FormatPagesStructureEditor) => void): void {
    const clone = this.selectedPagesEditor.clone();
    writer(clone);
    this.selectedPagesEditor = clone;
  }

  get currentPage(): FormatPageStructureEditor {
    return this.readPropertyOrDefault(s => s.getCurrentPage(), null);
  }

  set currentPage(value: FormatPageStructureEditor) {
    this.writePropertyWhenValid(s => s.updatePage(value));
  }

  get currentPageIndex(): number {
    return this.readPropertyOrDefault(s => s.getCurrentPageIndex(), null);
  }

  get currentScale(): number {
    return this.readPropertyOrDefault(s => s.getCurrentScale(), 1);
  }

  get allPagesFormatObjects(): IFormatObjectEditable[] {
     return Enumerable.from(this.pages)
      .selectMany(p => p.getObjects())
      .toArray();
  }

  get allPagesFormatObjectsForEachPage(): { pageKey: number, objects: IFormatObjectEditable[] } [] {
     return Enumerable.from(this.pages)
      .select(p => {
        return {
          pageKey: p.pageKey().getValue(),
          objects: p.getObjects(),
        };
      })
      .toArray();
  }

  updateAllPagesFormatObjects(
    where: (source: IFormatObjectEditable, pageKey: number) => boolean,
    writer: (source: IFormatObjectEditable, pageKey: number) => void): void {
    this.writePropertyWhenValid(s => {
      const pages = Enumerable.from(s.getPages())
        .select(p => {
          const objects = Enumerable.from(p.getObjects())
            .where(o => where(o, p.pageKey().getValue()))
            .select(o => o.clone())
            .toArray();
          if (objects.length === 0) { 
            return null;
          }
          const clone = p.clone();
          Enumerable.from(objects)
            .forEach(o => {
              writer(o, p.pageKey().getValue());
              clone.updateObject(o);
            });
          return clone;
        })
        .where(p => !!p)
        .toArray();
      if (pages.length === 0) {
        return;
      }
      s.updatePages(pages);
    });
  }

  updateFocusedObject(value: IFormatObjectEditable): void {
    const pages = this.readPropertyOrDefault(s => s.getPages(), null);
    if (!pages) { return; }
    const targetPage = Enumerable.from(pages)
      .firstOrDefault(p => 
        Enumerable.from(p.getObjects())
        .any(o => o.id().getValue() === value.id().getValue() 
          && o.name().getValue() === value.name().getValue()));
    if (!targetPage) { return; }
    this.currentPage = targetPage;
    this.currentPage.deselectAllObjects();
    this.currentPage.selectObjects(value);
  }

  get formatId(): number {
    return this.readPropertyOrDefault(s => s.id().getValue(), null);
  }

  get name(): string {
    return this.readPropertyOrDefault(s => s.name().getValue(), "");
  }

  set name(value: string) {
    this.writePropertyWhenValid(s => s.name().setValue(value));
  }

  get documentKindId(): number {
    return this.readPropertyOrDefault(s => s.documentKindId().getValue(), null);
  }

  set documentKindId(value: number) {
    this.writePropertyWhenValid(s => s.documentKindId().setValue(value));
  }

  get companyCode(): string {
    return this.readPropertyOrDefault(s => s.companyCode().getValue(), "");
  }

  set companyCode(value: string) {
    this.writePropertyWhenValid(s => s.companyCode().setValue(value));
  }

  get masterCode(): string {
    return this.readPropertyOrDefault(s => s.masterCode().getValue(), "");
  }

  set masterCode(value: string) {
    this.writePropertyWhenValid(s => s.masterCode().setValue(value));
  }

  get expirationDate(): DateDuration {
    return this.readPropertyOrDefault(s => DateDuration.from(s.expirationDate().getValue()), new DateDuration(null, null));
  }

  set expirationDate(value: DateDuration) {
    this.writePropertyWhenValid(s => s.expirationDate().setValue(DateDuration.to(value)));
  }

  get numberOfPrints(): number {
    return this.readPropertyOrDefault(s => s.numberOfPrints().getValue(), 1);
  }

  set numberOfPrints(value: number) {
    this.writePropertyWhenValid(s => s.numberOfPrints().setValue(value));
  }

  get indicationMagnification(): number {
    return this.readPropertyOrDefault(s => s.indicationMagnification().getValue(), 100);
  }

  set indicationMagnification(value: number) {
    this.writePropertyWhenValid(s => s.indicationMagnification().setValue(value));
  }

  get categoryType(): string {
    return this.readPropertyOrDefault(s => s.categoryType().getValue(), "1");
  }

  set categoryType(value: string) {
    this.writePropertyWhenValid(s => s.categoryType().setValue(value));
  }

  get tag(): string {
    return this.readPropertyOrDefault(s => s.tag().getValue(), "");
  }

  set tag(value: string) {
    this.writePropertyWhenValid(s => s.tag().setValue(value));
  }

  get inOutType(): string {
    return this.readPropertyOrDefault(s => s.inOutType().getValue(), "");
  }

  set inOutType(value: string) {
    this.writePropertyWhenValid(s => s.inOutType().setValue(value));
  }

  get referenceFiles(): IEditableFormatReferenceFile[] {
    return this.readPropertyOrDefault(s => s.referenceFiles().getValue(), []);
  }

  set referenceFiles(value: IEditableFormatReferenceFile[]) {
    this.writePropertyWhenValid(s => s.referenceFiles().setValue(value));
  }

  get lastMaxObjectId(): number {
    return this.readPropertyOrDefault(s => s.lastMaxObjectId().getValue(), 0);
  }

  get writeMethodType(): WriteMethodKinds {
    return this.readPropertyOrDefault(s => s.writeMethodType().getValue(), WriteMethodKinds.Unknown);
  }

  set writeMethodType(value: WriteMethodKinds) {
    this.writePropertyWhenValid(s => s.writeMethodType().setValue(value));
  }

  get nameWarningSetting(): IEditableFormatSetting {
    return this.readPropertyOrDefault(s => s.settings().getValue(), null);
  }

  set nameWarningSetting(value: IEditableFormatSetting) {
    this.writePropertyWhenValid(s => s.settings().setValue(value));
  }

  get questionnaire(): IEditableFormatQuestionnaire {
    return this.readPropertyOrDefault(s => s.questionnaire().getValue(), null);
  }

  set questionnaire(value: IEditableFormatQuestionnaire) {
    this.writePropertyWhenValid(s => s.questionnaire().setValue(value));
  }

  get alerts(): IFormatAlert[] {
    return this.readPropertyOrDefault(s => s.getAlerts(), []);
  }

  set alerts(value: IFormatAlert[]) {
    this.writePropertyWhenValid(s => s.setAlerts(value));
  }

  getPageNumber(pageKey: number): number {
    return this.allPagesFormatObjectsForEachPage.findIndex(p => p.pageKey === pageKey) + 1;
  }
}
